import Image from "next/image";
import Link from "next/link";
import { AddToCartButton } from "@/components/add-to-cart-button";
import { SoftFallback } from "@/components/content-blocks/band-helpers";
import {
  buildAnalyticsItemFromProduct,
  pushDataLayerEvent,
} from "@/lib/analytics";
import {
  buildProductPath,
  formatPrice,
  resolveMediaAlt,
  resolveOfferRate,
} from "@/lib/product-display";
import type { ProductListItem } from "@/lib/storefront-types";

type ProductCardProps = {
  analyticsItemListId?: string;
  analyticsItemListName?: string;
  product: ProductListItem;
};

export function ProductCard({
  analyticsItemListId,
  analyticsItemListName,
  product,
}: ProductCardProps) {
  const imageAlt = resolveMediaAlt(product.image?.alt, product.name);
  const description = product.summary || product.description;
  const addToCartLabel = `Add to cart - ${formatPrice(product.price)}`;
  // Offers follow the PRODUCT: the CMS only emits wasPrice/offerPercentageRate
  // when the product's On Offer switch is on, and it does so on every surface
  // — so the ribbon + was/sale treatment renders wherever the card does, and
  // the detail page carries the identical discounted price.
  const offerRate = resolveOfferRate(product);
  const wasPrice = typeof product.wasPrice === "number" ? product.wasPrice : null;
  const showOfferTreatment =
    offerRate !== null && wasPrice !== null && wasPrice > product.price;
  const productPath = buildProductPath(product);

  function handleSelectItem() {
    pushDataLayerEvent("select_item", {
      item_list_id: analyticsItemListId,
      item_list_name: analyticsItemListName,
      items: [
        buildAnalyticsItemFromProduct(product, {
          itemListId: analyticsItemListId,
          itemListName: analyticsItemListName,
        }),
      ],
    });
  }

  return (
    <article className="group relative flex h-full flex-col">
      {showOfferTreatment ? (
        <div
          aria-hidden="true"
          className="storefront-corner-ribbon-wrap pointer-events-none absolute z-30"
        >
          <div className="storefront-corner-ribbon">
            <span className="storefront-corner-ribbon-label">{offerRate}% Off</span>
          </div>
        </div>
      ) : null}

      {/* Explicit "view details" affordance — the ONLY route to the detail
          page, so the image / card body stay non-clickable for quick add-to-
          cart. The sr-only label keeps it a crawlable, descriptive internal
          link (SEO) while the visible target is just the icon. */}
      {productPath ? (
        <Link
          href={productPath}
          onClick={handleSelectItem}
          title="View details"
          className="absolute right-3 top-3 z-30 inline-flex h-9 w-9 items-center justify-center rounded-full border storefront-border-soft bg-white/95 text-[var(--storefront-primary)] shadow-[0_6px_16px_-8px_rgba(0,39,0,0.4)] backdrop-blur-sm transition-colors duration-200 hover:bg-[var(--storefront-primary)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--storefront-primary)]"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-[18px] w-[18px]"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span className="sr-only">View {product.name} details</span>
        </Link>
      ) : null}

      <div className="relative z-10 flex h-full flex-col overflow-hidden rounded-[1.6rem] border storefront-border-soft bg-white shadow-[0_18px_40px_-34px_rgba(0,39,0,0.26)] transition-[border-color,box-shadow] duration-200 group-hover:border-[#bfd0ba] group-hover:shadow-[0_26px_52px_-38px_rgba(0,39,0,0.3)]">
        <div className="relative flex h-[11.5rem] items-center justify-center overflow-hidden border-b border-[#eef2ea] bg-white p-4 sm:h-[12rem] sm:p-5">
          {product.image ? (
            <div className="relative h-full w-full">
              <Image
                src={product.image.url}
                alt={imageAlt}
                fill
                sizes="(min-width: 1024px) 45vw, (min-width: 640px) 44vw, 88vw"
                className="object-contain"
              />
            </div>
          ) : (
            <SoftFallback />
          )}
        </div>

        <div className="flex flex-1 flex-col items-center px-5 pb-5 pt-5 text-center sm:px-6 sm:pb-6">
          <div className="min-w-0 space-y-2">
            <h2 className="text-[1.28rem] font-semibold tracking-tight storefront-text-heading sm:text-[1.48rem]">
              {product.name}
            </h2>
            <p className="line-clamp-3 text-[0.97rem] leading-6 storefront-text-body sm:text-[1rem]">
              {description}
            </p>
          </div>

          {showOfferTreatment ? (
            <div className="mt-5 flex flex-wrap items-baseline justify-center gap-2 text-center">
              <span className="text-lg font-semibold text-[var(--storefront-primary)]">
                {formatPrice(product.price)}
              </span>
              {wasPrice !== null ? (
                <span className="text-sm font-medium text-[#d71920] line-through">
                  Was {formatPrice(wasPrice)}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="mt-auto flex w-full items-center justify-center pt-6">
            <AddToCartButton
              product={product.snipcart}
              label={addToCartLabel}
              onBeforeAddToCart={handleSelectItem}
              size="category"
            />
          </div>
        </div>
      </div>
    </article>
  );
}
